import * as H from "history";
import { MessageBarType } from "office-ui-fabric-react";
import { CommandBarButton } from "office-ui-fabric-react/lib-commonjs";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { DurableFunctionInstance } from "../../../../models/DurableFunctionInstance";
import Actions from "../../../../redux/Actions";
import { IRootState } from "../../../../store";
import DispatchProps from "../../../../utils/DispatchProps";
import ApiService from "../../../ApiService";
import LoadingCard from "../../../LoadingCard";
import MailroomDurableFunctionInstanceList from "../../../mailroom/MailroomDurableFunctionInstanceList";
import Table, { TableColumn } from "../../../Table";
import { Link } from "react-router-dom";
import TableCell from "../../../TableCell";
import SchoolCell from "../../../SchoolCell";
import AutomationControls from "../../../mailroom/AutomationControls";
import MailroomAutomationHistory from "../../../mailroom/MailroomAutomationHistory";
import School from "../../../../models/School";
import {SdsInfo} from "../../../../models/SdsInfo";
import MailroomSchoolsTable from "../../../mailroom/MailroomSchoolsTable";
import _ from "lodash";
import {IndustryDataRunAndActivityResults} from "../../../../models/industryData/IndustryDataRunAndActivityResults";
import {IndustryDataRunStatus} from "../../../../models/industryData/IndustryDataRun";
import {IndustryDataActivityStatus} from "../../../../models/industryData/InboundActivityResult";
import PageTitle from "../../../PageTitle";

interface MailroomAutomationPageProps {
    tenantId: string;
    profileId: string;

    apiService: ApiService;
    history: H.History;
}

interface MailroomAutomationPageState {
    sdsInfo?: SdsInfo;
    outboundRunsAndActivities?: IndustryDataRunAndActivityResults[]
    automationLoopInstance: DurableFunctionInstance | undefined | null;

    schoolsInAutomation?: { school: School,lastDataUpload: Date }[] | null;

    uploadInstances: DurableFunctionInstance[] | undefined;
    photosButtonDisabled: boolean;
}

class MailroomAutomationPage extends Component<MailroomAutomationPageProps & DispatchProps, MailroomAutomationPageState> {

    constructor(props: MailroomAutomationPageProps & DispatchProps) {
        super(props);

        this.state = {
            sdsInfo: undefined,
            outboundRunsAndActivities: undefined,
            automationLoopInstance: undefined,
            schoolsInAutomation: undefined,
            uploadInstances: undefined,
            photosButtonDisabled: false,
        };
    }

    async componentDidUpdate(prevProps: Readonly<MailroomAutomationPageProps & DispatchProps>, prevState: Readonly<MailroomAutomationPageState>, snapshot?: any): Promise<void> {
        if (this.props.tenantId !== prevProps.tenantId || this.props.profileId !== prevProps.profileId) {
            await this.loadsdsInfoAndLoopInstance();
            await this.loadUploadInstances();
            await this.loadSchoolsInAutomation();
        }
        if(this.state.sdsInfo !== prevState.sdsInfo)
        {
            await this.loadOutboundRunsAndActivities();
        }
    }

    async componentDidMount() {
        if (this.props.tenantId && this.props.profileId) {
            await this.loadsdsInfoAndLoopInstance();
            await this.loadUploadInstances();
            await this.loadSchoolsInAutomation();
        }
    }

    private async loadsdsInfoAndLoopInstance() {
        try {
            this.setState({ sdsInfo: undefined });
            const sdsInfo = await this.props.apiService.getSdsInfo(this.props.tenantId, this.props.profileId);
            this.setState({ sdsInfo: sdsInfo });
         
            this.setState({ automationLoopInstance: undefined });
            const automationLoopInstance = await this.props.apiService.getAutomation(this.props.tenantId,this.props.profileId);
            this.setState({ automationLoopInstance: automationLoopInstance });
        }
        catch (e) {
            this.setState({ sdsInfo: undefined, automationLoopInstance: null });
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Error loading automation`, MessageBarType.error));
        }
    }
    
    private async loadOutboundRunsAndActivities()
    {
        try {
            this.setState({ outboundRunsAndActivities: undefined });
            const outboundRunsAndActivities = await this.props.apiService.getOutboundRunsAndActivitiesForAutomation(this.props.tenantId, this.props.profileId);
            this.setState({ outboundRunsAndActivities: outboundRunsAndActivities });
        }
        catch (e) {
            this.setState({ outboundRunsAndActivities: undefined});
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Error loading outbound runs and stats`, MessageBarType.error));
        }
    }

    private async loadSchoolsInAutomation() {
        try {
            this.setState({ schoolsInAutomation: undefined });
            const schoolsInAutomation = await this.props.apiService.getSchoolsInProfile(this.props.tenantId, this.props.profileId);
            this.setState({ schoolsInAutomation: schoolsInAutomation });
        }
        catch (e) {
            this.setState({ schoolsInAutomation: null });
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Error loading automation status by flow`, MessageBarType.error));
        }
    }

    private onChangeStatusChanged = async (): Promise<void> => {
        this.setState({ sdsInfo: undefined, automationLoopInstance:  undefined });
        await new Promise(resolve => setTimeout(resolve, 2000)); // wait for fire and forget nature of orchestration events
        await this.loadsdsInfoAndLoopInstance();
    }

    private async loadUploadInstances() {
        this.setState({
            uploadInstances: undefined
        });

        const records = await this.props.apiService.getProfileMailroomFunctionInstances(this.props.tenantId, this.props.profileId, ["UploadOrchestrationNew"]); 

        this.setState({
            uploadInstances: records
        });
    }
    
    private automationColumns: TableColumn[] = [
        {
            title: "SDS Name",
            extraDetail: "SDS Flow",
            propertyPath: "sdsInfo.sdsId",
            flex: 4,
            enableSort: true
        },
        {
            title: "Tenant Name",
            propertyPath: "sdsInfo.tenantName",
            flex: 4,
            enableSort: true
        },
        {
            title: "Last SDS Upload",
            propertyPath: "sdsInfo.lastSdsUpload",
            type: "date",
            flex: 3,
            enableSort: true
        },
        {
            title: "Status",
            propertyPath: "automationLoopInstance.runtimeStatus",
            flex: 3,
            enableSort: true
        }
    ];

    private schoolColumns: TableColumn[] = [
        {
            title: "Name",
            propertyPath: "school.schoolName",
            flex: 4,
            enableSort: true
        },
        {
            title: "Data last received",
            propertyPath: "lastDataUpload",
            type: "date",
            flex: 3,
            enableSort: true
        },
        {
            title: "",
            propertyPath: "errorReportButton",
            flex: 1
        },
        {
            title: "",
            propertyPath: "startPhotosButton",
            flex: 1,
            enableSort: false
        }
    ];

    private cellForColumn = (item: { school: School,lastDataUpload: Date }, column: TableColumn, columnIndex: number): JSX.Element | undefined => {
        switch (column.propertyPath) {
            case "school":
                return <SchoolCell schoolKey={item.school!} school={item.school} enableLink={true} flex={column.flex} linkTo={`/cdb-admin/mailroom/${item.school!.tenantId}/${item.school!.schoolCode}`} />
                
            case "errorReportButton":
                return (
                    <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                        <Link to={`/manage/${item.school.tenantId}/${item.school.schoolCode}/school-data`}>

                            <CommandBarButton
                                iconProps={{ iconName: "reportdocument" }}
                                text="View Mailroom Error Report"
                                onClick={() => { }}
                            />
                        </Link>
                    </TableCell>
                );
            
            case "startPhotosButton":
                return <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                    <CommandBarButton text="Start Photos Workload" iconProps={{ iconName: "Photo2Add" }} onClick={(e) => { e.preventDefault(); this.startPhotosInstall(item.school!.tenantId!,item.school!.schoolCode!,this.props.profileId!)}} />
                </TableCell>;
        }
    }
    
    private renderAutomationTable() {
        switch (this.state.sdsInfo) {
            case undefined:
                return <LoadingCard />;
            case null:
                return <span>Error loading Mailroom profile information</span>;
            default:
                return <Table
                    title="Automation"
                    columns={this.automationColumns}
                    cellForColumn={this.automationTableCellForColumn}
                    items={[{sdsInfo: this.state.sdsInfo,automationLoopInstance: this.state.automationLoopInstance}]}
                    virtualize={false}>

                    <AutomationControls tenantId={this.props.tenantId} sdsProfileId={this.props.profileId} automationLoopInstance={this.state.automationLoopInstance} onChange={this.onChangeStatusChanged} />
                 </Table>                
        }
    }

    private renderSchoolsTable() {
        switch (this.state.schoolsInAutomation) {
            case undefined:
                return <LoadingCard />;
            case null:
                return <span>Error loading Mailroom schools information</span>;
            default:
                return <Table
                    title={`School(s) in SDS Flow`}
                    items={this.state.schoolsInAutomation}
                    columns={this.schoolColumns}
                    cellForColumn={this.cellForColumn}
                    rowPropsForItem={(tableItem) => {
                        return {
                            enableRowLink: true,
                            to: `/cdb-admin/mailroom/${tableItem["school"].tenantId}/${tableItem["school"].schoolCode}`
                        }
                    }}
                    virtualize={false}>
                </Table>
        }
    }
    
    private renderSdsRunsTable()
    {
        if(this.state.outboundRunsAndActivities === undefined)
        {
            return <LoadingCard text={"Loading..."}/>
        }
        
        const pillColourForRunStatus = (runStatus: IndustryDataRunStatus): string => {
            
            switch (runStatus)
            {
                case "running":
                    return "amber"
                
                case "failed":
                case "completedWithErrors":
                    return "red";
                    
                case "completed":
                case "completedWithWarnings":
                    return "green"
                
                case "unknownFutureValue":
                    return "grey";
            }
        }
        
        const pillColourForActivityStatus = (activityStatus: IndustryDataActivityStatus): string => {
            
            switch (activityStatus)
            {
                case "inProgress":
                    return "amber";
                    
                case "skipped":
                case "failed":
                case "completedWithErrors":
                    return "red";
                
                case "completed":
                case "completedWithWarnings":
                    return "green";
                    
                case "unknownFutureValue":
                default:
                    return "grey";
            }
        };
        
        
        const cellForColumn = (item: any,column:  TableColumn) => {
            
            if(column.propertyPath === "activityResultsForOutboundFlowset")
            {
                const activitySummaries = item.activityResultsForOutboundFlowset.map((activity: any) => {

                    const friendlyName = _.split(activity.displayName, " - ")[0];
                    return (
                            <span>{friendlyName} <span className={`pill pill--tiny pill--${pillColourForActivityStatus(activity.status)}`}>{activity.status}</span></span>
                    );
                });

                return (
                    <TableCell flex={column.flex}>
                        {activitySummaries}
                    </TableCell>
                );
            }
            
            if(column.propertyPath == "industryDataRun.status")
            {
                return (
                    <TableCell flex={column.flex}>
                        <span className={`pill pill--${pillColourForRunStatus(item.industryDataRun.status)}`}>{item.industryDataRun.status}</span>
                    </TableCell>
                );
            }
        };

        return (<Table title="SDS Runs For Flow" columns={[
            {
                title: "Start",
                propertyPath: "industryDataRun.startDateTime",
                type: "date",
                flex: 1
            },
            {
                title: "End",
                propertyPath: "industryDataRun.endDateTime",
                type: "date",
                flex: 1
            },
            {
                title: "Activites",
                propertyPath: "activityResultsForOutboundFlowset",
                flex: 2
            },
            {
                title: "Status",
                propertyPath: "industryDataRun.status",
                flex: 1
            }
            ]} items={this.state.outboundRunsAndActivities} cellForColumn={cellForColumn}/>);
    }

    private renderMailroomAutomationHistory() {
        return (
            <MailroomAutomationHistory
                    tenantId={this.props.tenantId}
                    profileId={this.props.profileId} />
        );
    }

    private async startPhotosInstall(tenantId: string,schoolCode: string, sdsProfileId: string) {
        this.setState({
            photosButtonDisabled: true
        });

        try {
            const workloadId = await this.props.apiService.triggerPhotoInstallWorkload(tenantId, schoolCode, sdsProfileId);

            this.props.dispatch(Actions.showMessageBar(`Started Photos Workload For School`, MessageBarType.success));
            window.open(`/cdb-admin/workloads/${workloadId}`, "_blank");
        }
        catch (e) {
            console.log(e)
            this.props.dispatch(Actions.showMessageBar(`Failed to start photos workload for school: ${e.message}`, MessageBarType.error));
        }

        this.setState({
            photosButtonDisabled: false
        });
    }

    private automationTableCellForColumn = (item: { sdsInfo: SdsInfo,automationLoopInstance: DurableFunctionInstance }, column: TableColumn, columnIndex: number): JSX.Element | undefined =>
    {
        switch (column.propertyPath) {
            case "sdsInfo.tenantName":
                return <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                    <span>{item.sdsInfo.tenantName}</span>
                    <span className={"cdb-table__extra-detail"}>{item.sdsInfo.tenantId}</span>
                </TableCell>;
            
            case "automationLoopInstance.runtimeStatus":
                return <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                    {MailroomSchoolsTable.AutomationLoopStatusCell(item.automationLoopInstance)}
                </TableCell>;

            case "sdsInfo.sdsId":
                return <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                    <span>{item.sdsInfo.sdsName}</span>
                    <span className={"cdb-table__extra-detail"}>{item.sdsInfo.sdsId}</span>
                </TableCell>;

            case "sdsInfo.lastSdsUpload":
                return <TableCell key={`${columnIndex}-${column.propertyPath}`} flex={column.flex}>
                    {MailroomSchoolsTable.TimeSinceCell(item.sdsInfo.lastSdsUpload)}
                </TableCell>;
        }
    }

    render() {
        return (
            <Fragment>
                <PageTitle title={this.state.sdsInfo?.tenantName ? `${this.state.sdsInfo?.tenantName} - Automation` : `Automation`} />

                {this.renderAutomationTable()}

                {this.renderSchoolsTable()}

                <MailroomDurableFunctionInstanceList title={"Uploads (Past 30 days)"} instances={this.state.uploadInstances} columns={MailroomDurableFunctionInstanceList.uploadColumns} enableRowLinks={true} linkForInstance={(dfi) => `/cdb-admin/mailroom/${this.props.tenantId}/upload/${dfi.durableFunctionInstanceId}`}>
    
                </MailroomDurableFunctionInstanceList>

                {this.renderSdsRunsTable()}

                {this.renderMailroomAutomationHistory()}
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { history: H.History, match: any }): MailroomAutomationPageProps => {

        const tenantId = ownProps.match.params.tenantId;
        const profileId = ownProps.match.params.profileId;

        return {
            tenantId: tenantId,
            profileId: profileId,

            apiService: state.auth.apiService,
            history: ownProps.history
        };
    }
)(MailroomAutomationPage);